/**
 * The Director sheet: a compact broadcast-desk bottom sheet. Sensible defaults mean "Go to air"
 * works on the first tap; every control is optional. Voices come from `GET /director/voices`
 * only — when the server cannot speak, the sheet says so instead of offering pretend voices.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { DIRECTOR_ENERGIES, DIRECTOR_LENGTHS, DIRECTOR_SPOILERS, DIRECTOR_STYLES, directorTeamName, type DirectorLanguage, type DirectorVoice } from '@clutch/core';
import { useClient } from '../client';
import { TeamLogo } from '../ui/Media';
import { teamAccent } from '../ui/color';
import type { DirectorCapabilities } from './api';
import { htmlLang, languageLabel, offeredLanguages, pick } from './i18n';
import {
  ENERGY_COPY, FAMILIARITY_COPY, LENGTH_COPY, STYLE_COPY, VoicePreview, selectedVoice, voiceGate,
  writerConnected, type AudioLike, type Familiarity, type Focus, type ShowDraft, type VoiceGate,
} from './show';

type Caps = DirectorCapabilities | undefined;
let capsOnce: Promise<Caps> | undefined;
let voicesOnce: Promise<DirectorVoice[] | undefined> | undefined;

/** Capabilities + the voice catalog, fetched once per page load and only when speech exists. */
export function useStudio(): { caps: Caps | 'loading'; voices: DirectorVoice[] | undefined | 'loading' } {
  const client = useClient();
  const api = client.director;
  const [caps, setCaps] = useState<Caps | 'loading'>(api ? 'loading' : undefined);
  const [voices, setVoices] = useState<DirectorVoice[] | undefined | 'loading'>('loading');
  useEffect(() => {
    if (!api) return;
    let alive = true;
    capsOnce ??= api.capabilities().catch(() => undefined);
    void capsOnce.then((c) => {
      if (!alive) return;
      setCaps(c);
      if (!c?.speechConfigured || !api.voices) return setVoices(undefined);
      voicesOnce ??= api.voices().catch(() => undefined);
      void voicesOnce.then((v) => { if (alive) setVoices(v); });
    });
    return () => { alive = false; };
  }, [api]);
  return { caps, voices };
}
/** Tests reset the per-page memo between mounts. */
export function resetStudioCache() { capsOnce = undefined; voicesOnce = undefined; }

const T = {
  en: { direct: 'Direct this game', remix: 'Remix this take', style: 'Performance', energy: 'Intensity', voice: 'Voice', language: 'Language', knowledge: 'Knowledge', length: 'Length', focus: 'Lens', spoilers: 'Spoilers', upToNow: 'Catch me up to now', fullStory: 'Full game story', go: 'Create my broadcast', regen: 'Create this take', sample: 'Every voice reads the same verified line, so you hear only the voice change.', noSample: 'No sample for this voice yet.', preview: 'Preview', stop: 'Stop', notConnected: 'Narration is not connected on this server', notConnectedSub: 'Create my broadcast will report “Voice unavailable”. No device voice will pretend to be a narrator.', house: 'Server voice', houseSub: 'This server narrates with its own configured AI voice.', checking: 'Checking the voice booth…', turning: 'Game drama', analysis: 'Swings & tactics', calls: 'My calls', player: 'A player', playerPh: 'Follow a player', skip: 'Defaults are ready — tap Create my broadcast.', noWriter: 'AI Director not connected on this server.', noWriterSub: 'No broadcast can be created until a model is configured — Clutch never substitutes a template story.', fanOf: (t: string) => `${t} fan` },
  hi: { direct: 'यह मैच डायरेक्ट करें', remix: 'इस टेक को रीमिक्स करें', style: 'प्रस्तुति', energy: 'तीव्रता', voice: 'आवाज़', language: 'भाषा', knowledge: 'जानकारी', length: 'लंबाई', focus: 'नज़रिया', spoilers: 'स्पॉइलर', upToNow: 'अभी तक का हाल', fullStory: 'पूरे मैच की कहानी', go: 'मेरा ब्रॉडकास्ट बनाएँ', regen: 'यह टेक बनाएँ', sample: 'हर आवाज़ एक ही सत्यापित पंक्ति पढ़ती है।', noSample: 'इस आवाज़ का नमूना अभी नहीं है।', preview: 'सुनें', stop: 'रोकें', notConnected: 'इस सर्वर पर नैरेशन जुड़ा नहीं है', notConnectedSub: 'ब्रॉडकास्ट बनाने पर “आवाज़ उपलब्ध नहीं” दिखेगा। कोई डिवाइस आवाज़ नैरेटर नहीं बनेगी।', house: 'सर्वर की आवाज़', houseSub: 'यह सर्वर अपनी तय AI आवाज़ में सुनाता है।', checking: 'वॉइस बूथ जाँच रहे हैं…', turning: 'मैच का रोमांच', analysis: 'बदलाव और रणनीति', calls: 'मेरी कॉल', player: 'एक खिलाड़ी', playerPh: 'खिलाड़ी फ़ॉलो करें', skip: 'डिफ़ॉल्ट तैयार हैं।', noWriter: 'इस सर्वर पर AI डायरेक्टर जुड़ा नहीं है।', noWriterSub: 'मॉडल कॉन्फ़िगर होने तक कोई ब्रॉडकास्ट नहीं बनेगा — Clutch कभी टेम्पलेट कहानी नहीं दिखाता।', fanOf: (t: string) => `${t} फ़ैन` },
  zh: { direct: '导演这场比赛', remix: '重新混剪', style: '演绎方式', energy: '强度', voice: '声音', language: '语言', knowledge: '了解程度', length: '时长', focus: '视角', spoilers: '剧透', upToNow: '补到当前', fullStory: '完整比赛故事', go: '生成我的节目', regen: '生成这一版', sample: '每个声音朗读同一句经过核实的台词。', noSample: '该声音暂无试听。', preview: '试听', stop: '停止', notConnected: '此服务器未连接解说语音', notConnectedSub: '生成节目时将提示“语音不可用”，不会用设备语音冒充解说员。', house: '服务器默认语音', houseSub: '此服务器使用其配置的AI语音解说。', checking: '正在检查录音间…', turning: '比赛悬念', analysis: '转折与战术', calls: '我的预测', player: '球员', playerPh: '关注球员', skip: '默认设置已就绪。', noWriter: '此服务器未连接AI导演。', noWriterSub: '在配置模型之前无法生成节目——Clutch不会用模板故事替代。', fanOf: (t: string) => `${t}球迷` },
};
export const sheetCopy = (l: DirectorLanguage) => pick(T, l);

export type SheetGame = { away: string; home: string; status?: string; players?: string[] };

export function DirectorSheet({ game, draft, onChange, onGenerate, onClose, mode = 'direct', busy = false }: {
  game: SheetGame;
  draft: ShowDraft;
  onChange: (d: ShowDraft) => void;
  onGenerate: (d: ShowDraft, gate: VoiceGate) => void;
  onClose?: () => void;
  mode?: 'direct' | 'remix';
  busy?: boolean;
}) {
  const l = draft.language;
  const c = sheetCopy(l);
  const client = useClient();
  const { caps, voices } = useStudio();
  const gate = voiceGate(caps === 'loading' ? 'loading' : caps, voices, l);
  const langs = offeredLanguages(caps === 'loading' ? undefined : caps);
  const set = (patch: Partial<ShowDraft>) => onChange({ ...draft, ...patch });
  const audio = useRef<HTMLAudioElement>(null);
  const [preview, setPreview] = useState<VoicePreview>();
  const [playing, setPlaying] = useState<string>();
  const [fetching, setFetching] = useState<string>();
  const [previewError, setPreviewError] = useState<string>();
  const samples = useRef(new Map<string, string | undefined>());
  const previewRequest = useRef(0);
  const previewAlive = useRef(false);
  const [catalogPreview, setCatalogPreview] = useState(false);
  const [playerName, setPlayerName] = useState(draft.focus.kind === 'player' ? draft.focus.name : '');
  useEffect(() => {
    if (!audio.current) return;
    previewAlive.current = true;
    const p = new VoicePreview(audio.current as unknown as AudioLike);
    setPreview(p);
    const off = p.subscribe(setPlaying);
    return () => {
      previewAlive.current = false; previewRequest.current++;
      off(); p.stop();
      for (const url of samples.current.values()) if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
      samples.current.clear();
    };
  }, []);
  /** ▶ plays the same verified basketball line in this voice; the provider's own preview only on 404. */
  const play = async (v: DirectorVoice) => {
    const revision = ++previewRequest.current;
    preview?.stop();
    if (playing === v.voiceId) return;
    const key = `${v.voiceId}|${l}`;
    setPreviewError(undefined);
    if (!samples.current.has(key)) {
      setFetching(v.voiceId);
      try {
        const url = await client.director?.voiceSample?.(v.voiceId, l);
        if (!previewAlive.current) { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); return; }
        samples.current.set(key, url);
      } catch {
        if (!previewAlive.current || previewRequest.current !== revision) return;
        setFetching(undefined);
        setPreviewError(v.voiceId);
        return;
      }
    }
    if (!previewAlive.current || previewRequest.current !== revision) return;
    setFetching(undefined);
    const url = samples.current.get(key) ?? v.previewUrl;
    if (!url) return setPreviewError(v.voiceId);
    setCatalogPreview(!samples.current.get(key));
    preview?.toggle(v.voiceId, url);
  };
  const chosen = selectedVoice(gate, draft.voiceId);
  const focusKey = (f: Focus) => (f.kind === 'team' ? `team-${f.team}` : f.kind);
  const summary = [pick(STYLE_COPY[draft.style].name, l), pick(ENERGY_COPY[draft.energy], l), gate.state === 'ready' ? chosen?.name ?? gate.voices[0]?.name : gate.state === 'house' ? c.house : undefined, pick(LENGTH_COPY[draft.length], l)].filter(Boolean).join(' · ');
  const style = { '--a': teamAccent(game.away), '--h': teamAccent(game.home) } as CSSProperties;
  const players = useMemo(() => [...new Set([...(game.players ?? []), ...(draft.focus.kind === 'player' ? [draft.focus.name] : [])])].slice(0, 3), [game.players, draft.focus]);
  const lenses: Focus[] = [{ kind: 'team', team: game.away }, { kind: 'team', team: game.home }, ...players.map((name) => ({ kind: 'player', name }) as Focus), { kind: 'game' }, { kind: 'analysis' }, { kind: 'calls' }];

  return (
    <div className="ds-scrim" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }} data-testid="director-sheet-scrim">
      <section className="ds" role="dialog" aria-modal="true" aria-label={mode === 'remix' ? c.remix : c.direct} data-testid="director-sheet" data-mode={mode} style={style} lang={htmlLang(l)}>
        <audio ref={audio} preload="none" />
        <header className="ds__head">
          <span className="ds__grab" aria-hidden="true" />
          <p className="ds__slate"><TeamLogo tricode={game.away} size={16} />{game.away} @ {game.home}<TeamLogo tricode={game.home} size={16} />{game.status ? <span> · {game.status}</span> : null}</p>
          <h2 className="ds__title">{mode === 'remix' ? c.remix : c.direct}</h2>
          {onClose && <button className="ds__x" onClick={onClose} aria-label="Close" disabled={busy} data-testid="director-sheet-close">✕</button>}
        </header>

        <div className="ds__body">
          {caps !== 'loading' && writerConnected(caps) === false && (
            <p className="ds__note" role="status" data-testid="writer-not-connected"><b>{c.noWriter}</b> {c.noWriterSub}</p>
          )}
          <Group label={c.voice}>
            {gate.state === 'ready' && draft.voiceId && !chosen && <p className="ds__note" role="alert">{pick({ en: 'Your saved voice is no longer available. Choose a voice below.', hi: 'आपकी सेव की गई आवाज़ अब उपलब्ध नहीं है। नीचे दूसरी आवाज़ चुनें।', zh: '之前保存的声音已不可用，请在下方选择其他声音。' }, l)}</p>}
            {gate.state === 'checking' && <div className="ds__voices ds__voices--skeleton" aria-busy="true" aria-label={c.checking}><span /><span /><span /></div>}
            {gate.state === 'not-connected' && (
              <div className="ds__offair" role="status" data-testid="voice-not-connected">
                <b>{c.notConnected}</b>
                <small>{c.notConnectedSub}</small>
              </div>
            )}
            {gate.state === 'house' && (
              <div className="ds__offair ds__offair--house" role="status" data-testid="voice-house">
                <b>{c.house}</b>
                <small>{c.houseSub}</small>
              </div>
            )}
            {gate.state === 'ready' && (
              <div className="ds__voices ds__carousel" role="radiogroup" aria-label={c.voice} data-testid="voice-list">
                {gate.voices.map((v, i) => {
                  const on = draft.voiceId ? draft.voiceId === v.voiceId : i === 0;
                  const isPlaying = playing === v.voiceId;
                  return (
                    <div key={v.voiceId} className={`ds__voice${on ? ' is-on' : ''}${isPlaying ? ' is-playing' : ''}`} data-testid="voice-card" data-voice-id={v.voiceId}>
                      <button className="ds__voice-pick" role="radio" aria-checked={on} onClick={() => set({ voiceId: v.voiceId })} data-testid={`voice-${v.voiceId}`}>
                        <span className="ds__mic" aria-hidden="true">{v.name.slice(0, 1)}</span>
                        <b>{v.name}</b>
                        <span className="ds__chips">{[v.accent, v.gender, v.age].filter(Boolean).slice(0, 2).map((x) => <i key={x}>{x}</i>)}</span>
                        {v.description && <small>{v.description}</small>}
                      </button>
                      <button className="ds__play" onClick={() => void play(v)} aria-label={`${isPlaying ? c.stop : c.preview} ${v.name}`} aria-pressed={isPlaying} aria-busy={fetching === v.voiceId} data-testid={`voice-preview-${v.voiceId}`}>
                        {isPlaying ? <span className="ds__eq" aria-hidden="true"><i /><i /><i /></span> : fetching === v.voiceId ? <span className="ds__spin" aria-hidden="true" /> : <PlayIcon />}
                      </button>
                      {previewError === v.voiceId && <small className="ds__voice-err" role="status">{c.noSample}</small>}
                    </div>
                  );
                })}
              </div>
            )}
            {gate.state === 'ready' && <p className="ds__fine">{catalogPreview ? pick({ en: 'Provider catalog preview. This sample is not the game narration.', hi: 'प्रदाता की आवाज़ का नमूना। यह मैच का नैरेशन नहीं है।', zh: '声音库试听，此样本并非比赛解说。' }, l) : c.sample}</p>}
          </Group>

          <Group label={c.style}>
            <div className="ds__styles" role="radiogroup" aria-label={c.style}>
              {DIRECTOR_STYLES.map((s) => (
                <button key={s} role="radio" aria-checked={draft.style === s} className={`ds__style ds__style--${s}${draft.style === s ? ' is-on' : ''}`} onClick={() => set({ style: s })} data-testid={`style-${s}`}>
                  <span className="ds__style-glyph" aria-hidden="true"><StyleGlyph s={s} /></span>
                  <b>{pick(STYLE_COPY[s].name, l)}</b>
                  <small>{pick(STYLE_COPY[s].tag, l)}</small>
                </button>
              ))}
            </div>
          </Group>

          <Group label={c.energy}>
            <div className="ds__seg" role="radiogroup" aria-label={c.energy}>
              {DIRECTOR_ENERGIES.map((e, i) => (
                <button key={e} role="radio" aria-checked={draft.energy === e} className={draft.energy === e ? 'is-on' : ''} onClick={() => set({ energy: e })} data-testid={`energy-${e}`}>
                  <span className="ds__meter" aria-hidden="true">{[0, 1, 2].map((k) => <i key={k} className={k <= i ? 'is-lit' : ''} />)}</span>{pick(ENERGY_COPY[e], l)}
                </button>
              ))}
            </div>
          </Group>

          <Group label={c.language}>
            <div className="ds__seg ds__seg--sm" role="radiogroup" aria-label={c.language}>
              {langs.map((x) => <button key={x} role="radio" aria-checked={draft.language === x} className={draft.language === x ? 'is-on' : ''} onClick={() => set({ language: x })} data-testid={`sheet-lang-${x}`} {...(x !== 'en' ? { lang: htmlLang(x) } : {})}>{languageLabel(x)}</button>)}
            </div>
          </Group>

          <Group label={c.focus}>
            <div className="ds__focus" role="radiogroup" aria-label={c.focus}>
              {lenses.map((f) => {
                const on = focusKey(f) === focusKey(draft.focus) && (f.kind !== 'player' || draft.focus.kind === 'player' && draft.focus.name === f.name);
                return (
                  <button key={f.kind === 'player' ? `p-${f.name}` : focusKey(f)} role="radio" aria-checked={on} className={`ds__chip${on ? ' is-on' : ''}`} onClick={() => set({ focus: f })} data-testid={`focus-${f.kind === 'player' ? 'player' : focusKey(f)}`} style={f.kind === 'team' ? { '--t': teamAccent(f.team) } as CSSProperties : undefined}>
                    {f.kind === 'team' && <TeamLogo tricode={f.team} size={18} />}
                    {f.kind === 'team' ? c.fanOf(directorTeamName(f.team, l)) : f.kind === 'player' ? f.name : f.kind === 'game' ? c.turning : f.kind === 'analysis' ? c.analysis : c.calls}
                  </button>
                );
              })}
            </div>
            <form className="ds__player" onSubmit={(e) => { e.preventDefault(); if (playerName.trim()) set({ focus: { kind: 'player', name: playerName.trim().slice(0, 80) } }); }}>
              <input value={playerName} maxLength={80} placeholder={c.playerPh} aria-label={c.player} onChange={(e) => setPlayerName(e.target.value)} data-testid="focus-player-input" />
              <button type="submit" disabled={!playerName.trim()}>+</button>
            </form>
          </Group>

          <div className="ds__row ds__row--2">
            <Group label={c.length}>
              <div className="ds__seg ds__seg--sm" role="radiogroup" aria-label={c.length}>
                {DIRECTOR_LENGTHS.map((x) => <button key={x} role="radio" aria-checked={draft.length === x} className={draft.length === x ? 'is-on' : ''} onClick={() => set({ length: x })} data-testid={`length-${x}`}>{pick(LENGTH_COPY[x], l)}</button>)}
              </div>
            </Group>
            <Group label={c.knowledge}>
              <div className="ds__seg ds__seg--sm" role="radiogroup" aria-label={c.knowledge}>
                {(['new', 'regular', 'expert'] as Familiarity[]).map((x) => <button key={x} role="radio" aria-checked={draft.familiarity === x} className={draft.familiarity === x ? 'is-on' : ''} onClick={() => set({ familiarity: x })} data-testid={`knowledge-${x}`}>{pick(FAMILIARITY_COPY[x], l)}</button>)}
              </div>
            </Group>
          </div>

          <Group label={c.spoilers}>
            <div className="ds__seg ds__seg--sm" role="radiogroup" aria-label={c.spoilers}>
              {DIRECTOR_SPOILERS.map((x) => <button key={x} role="radio" aria-checked={draft.spoilers === x} className={draft.spoilers === x ? 'is-on' : ''} onClick={() => set({ spoilers: x })} data-testid={`spoilers-${x}`}>{x === 'through-cutoff' ? c.upToNow : c.fullStory}</button>)}
            </div>
          </Group>
        </div>

        <footer className="ds__foot">
          <p className="ds__summary" data-testid="director-summary">{summary}</p>
          <button className="ds__go" onClick={() => { preview?.stop(); onGenerate(gate.state === 'ready' && !draft.voiceId && gate.voices[0] ? { ...draft, voiceId: gate.voices[0].voiceId } : draft, gate); }} disabled={busy} data-testid="director-generate">
            {mode === 'remix' ? c.regen : c.go}
          </button>
        </footer>
      </section>
    </div>
  );
}

function PlayIcon() {
  return <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg>;
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return <fieldset className="ds__group"><legend>{label}</legend>{children}</fieldset>;
}

function StyleGlyph({ s }: { s: (typeof DIRECTOR_STYLES)[number] }) {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (s === 'play_by_play') return <svg viewBox="0 0 24 24" width="22" height="22"><rect x="9" y="3" width="6" height="11" rx="3" {...p} /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" {...p} /></svg>;
  if (s === 'analyst') return <svg viewBox="0 0 24 24" width="22" height="22"><path d="M4 20h16M6 16l4-5 3 3 5-7" {...p} /><circle cx="18" cy="7" r="1.5" fill="currentColor" /></svg>;
  if (s === 'storyteller') return <svg viewBox="0 0 24 24" width="22" height="22"><rect x="3" y="6" width="18" height="13" rx="2" {...p} /><path d="M3 10h18M7 6l2 4M12 6l2 4M17 6l2 4" {...p} /></svg>;
  return <svg viewBox="0 0 24 24" width="22" height="22"><path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.6-7 10-7 10z" {...p} /></svg>;
}
